import {
  CHANNEL_CATEGORIES,
  CHANNEL_KINDS,
  ChannelStatus
} from '@/models/channels'
import { configTypes } from '@/models/featureConfig'
import { shouldShowAllMessages } from '@/utils/channel'
import { getUniqueFromArray } from '@/utils/unique'
import DatabaseFactory from '@/wrappers/database'
import { addErrorLog } from '@/wrappers/database/utils/addLog'

import {
  getMessages,
  mountChannelDrawerQuery,
  mountChatDrawerQuery
} from './utils'

const fetchInitialMessages = async (
  store,
  { channelId, showAllMessages = true }
) => {
  const database = new DatabaseFactory(
    `channels/${channelId}/messages`
  ).getReadDatabase()
  try {
    const filters = showAllMessages
      ? []
      : [
          {
            field: 'channelCategory',
            condition: '==',
            value: CHANNEL_CATEGORIES.ADMINISTRATIVE
          }
        ]

    const query = {
      filters,
      orderBy: [{ field: 'createdAt', sort: 'asc' }],
      limit: 5
    }
    const snapshot = await database.get(query)

    if (!snapshot.docs.length) return ['Nenhuma mensagem']
    return getMessages(snapshot.docs)
  } catch (error) {
    addErrorLog({
      context: 'drawer/actions - fetchInitialMessages',
      channelId,
      error
    })
  }
}

const fetchChats = async store => {
  await store.dispatch('clearChatsDrawer')
  const database = new DatabaseFactory('channels').getReadDatabase()

  const query = mountChatDrawerQuery(
    store.rootState.channel.staff.loggedStaff,
    store.state.currentSortBy?.firestoreSort
  )

  const redirectAssistToAdminLimitDate = await store.dispatch(
    'featureConfig/getFeatureFlagById',
    {
      id: 'ehr-enable_redirect_assistance_admin_date',
      type: configTypes.STRING
    },
    { root: true }
  )
  const viewAllChannelMessages =
    store.rootState.permissions.channels.staff.viewAllChannelMessages

  const chatsListener = await database.getStream(
    query,
    async snapshot => {
      const docs = await Promise.all(
        snapshot.docs.map(async doc => {
          const showAllMessages = shouldShowAllMessages({
            channel: doc.data(),
            featureFlagValue: redirectAssistToAdminLimitDate,
            staffCanViewAllMessages: viewAllChannelMessages
          })

          const previewableMessages = await store.dispatch(
            'fetchInitialMessages',
            { channelId: doc.id, showAllMessages }
          )
          return {
            id: doc.id,
            previewableMessages,
            ...doc.data()
          }
        })
      )

      store.commit('setNewChats', docs)
      store.dispatch('fetchMembersInfo', {
        docs,
        leanResponse: false
      })
    },
    error =>
      addErrorLog({
        context: 'drawer/actions - fetchChats',
        error
      })
  )

  store.commit('setNewChatsListener', chatsListener)
}

const fetchChannels = (
  store,
  {
    kind = CHANNEL_KINDS.CHANNEL,
    mutation,
    privateUnreadMentions,
    starred,
    status = ChannelStatus.ACTIVE
  }
) => {
  try {
    const {
      currentFilters,
      currentPage,
      currentSortBy: { firestoreSort }
    } = store.state
    const staffId = store.rootState.user.user.id

    if (!staffId || !currentPage) return

    const database = new DatabaseFactory(
      `staff/${staffId}/channels`
    ).getReadDatabase()

    const query = mountChannelDrawerQuery(
      currentFilters,
      kind,
      status,
      starred,
      privateUnreadMentions,
      firestoreSort,
      currentPage
    )

    const listener = database.getStream(
      query,
      snapshot => {
        const { docs } = snapshot
        store.commit(mutation, docs)

        store.dispatch('fetchMembersInfo', {
          docs,
          leanResponse: true
        })
      },
      error =>
        addErrorLog({
          context: 'drawer/actions - fetchChannels',
          error
        })
    )

    return listener
  } catch (error) {
    throw new Error(`Error on drawer/fetchChannels: ${error}`)
  }
}
// firestore doesn't have all data we need, for our members we only have a
// channelPersonId - which is a different value from personId - but we must
// show the member's name and healthcare team for new chats; that's why we get
// this info from channel-bff-api each time we load new or more channels
const fetchMembersInfo = async (store, { docs = [], leanResponse }) => {
  if (docs.length === 0) return

  const channelPersonIds = docs
    ?.map(doc => {
      const channel = doc.data ? doc.data() : doc
      return channel.channelPersonId
    })
    .filter(getUniqueFromArray)

  await store.dispatch(
    'member/loadByChannelPersonIds',
    {
      channelPersonIds,
      leanResponse
    },
    { root: true }
  )
}

const setCurrentPage = (store, page) => {
  store.commit('setCurrentPage', page)
}

const clearCurrentFilters = store => {
  store.commit('setCurrentFilters', {
    tags: [],
    status: [],
    channelPersonId: undefined
  })
}

const clearChatsDrawer = store => {
  store.commit('setNewChats', undefined)
  store.commit('clearNewChatsListener')
}

const clearActiveChannels = store => {
  store.commit('setCurrentPage', 0)
  store.commit('setActiveChannels', undefined)
}

export default {
  fetchInitialMessages,
  fetchChats,
  fetchChannels,
  fetchMembersInfo,
  clearCurrentFilters,
  clearChatsDrawer,
  setCurrentPage,
  clearActiveChannels
}
